import type { ICellRendererParams } from '@ag-grid-community/core';
import type { ReactNode } from 'react';
import { Button, Icon, type IconTypes } from '@ics-portal/guardian';

export enum ActionButtonType {
    Edit = 'EDIT',
    View = 'VIEW',
    DUPLICATE = 'DUPLICATE',
    Delete = 'DELETE',
}

const ActionButtonTypeIcon: Record<ActionButtonType, IconTypes> = {
    [ActionButtonType.DUPLICATE]: 'control_point_duplicate',
    [ActionButtonType.Edit]: 'edit',
    [ActionButtonType.View]: 'info',
    [ActionButtonType.Delete]: 'delete',
};

export interface ColumnActionSettings<T> {
    customActionRenderer?: (params: ICellRendererParams<T>) => ReactNode;
    callbacks?: { [K in ActionButtonType]?: CallbackHandler<T> };
}

interface CallbackHandler<T> {
    showButton: (data?: T) => boolean;
    buttonCallback: (data?: T) => void;
}

export function ActionCellRenderer<T>(
    params: ICellRendererParams<T>,
    columnActionsSettings?: ColumnActionSettings<T>,
): ReactNode {
    function extractCallbackHandler(
        actionButtonType: ActionButtonType,
    ): CallbackHandler<T> | undefined {
        const callbacks = columnActionsSettings?.callbacks;
        if (!callbacks) {
            return;
        }

        return callbacks[actionButtonType];
    }

    return Object.values(ActionButtonType)
        .map((actionButtonType) => {
            const callbackHandler = extractCallbackHandler(actionButtonType);

            const buttonCallback = callbackHandler?.buttonCallback;
            const showButton = callbackHandler?.showButton;
            if (!buttonCallback || !showButton) {
                return;
            }

            if (!showButton(params.data)) {
                return;
            }

            const handleButtonClick = (): void => {
                buttonCallback(params.data);
            };

            return (
                <div
                    key={actionButtonType}
                    className="flex justify-content-center align-items-center h-full"
                >
                    <Button
                        text
                        onClick={handleButtonClick}
                        icon={
                            <Icon
                                className="cursor-pointer"
                                name={ActionButtonTypeIcon[actionButtonType]}
                            />
                        }
                    />
                </div>
            );
        })
        .find(Boolean);
}
